import axios from 'axios';
import API_BASE_URL from './apiConfig';

// Global state to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  // Redirect to login page
  window.location.href = '/'; // Redirect to home page
};

// --- AXIOS INTERCEPTOR ---
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 and the request hasn't been retried yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Prevent infinite loops if the refresh token endpoint itself returns 401
      if (originalRequest.url.includes('/token/refresh/')) {
        handleLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        handleLogout();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
          refresh: refreshToken
        }, {
           headers: { 'Content-Type': 'application/json' }
        });

        const newAccessToken = response.data.access;
        localStorage.setItem('access_token', newAccessToken);
        
        // If the backend rotates refresh tokens, update the refresh token too
        if (response.data.refresh) {
            localStorage.setItem('refresh_token', response.data.refresh);
        }

        processQueue(null, newAccessToken);
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        return axios(originalRequest);
        
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// --- FETCH OVERRIDE ---
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  let [resource, config] = args;

  const response = await originalFetch(resource, config);

  // Check if it's a 401 Unauthorized
  if (!response.ok && response.status === 401) {
    // Check to avoid infinite loop on refresh endpoint itself
    const urlString = typeof resource === 'string' ? resource : resource?.url || '';
    if (urlString.includes('/token/refresh/')) {
        handleLogout();
        return response;
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        // Retry the request with the new token
        config = config || {};
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${token}`
        };
        return originalFetch(resource, config);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    isRefreshing = true;
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
        handleLogout();
        return response;
    }

    try {
        const refreshResponse = await originalFetch(`${API_BASE_URL}/token/refresh/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ refresh: refreshToken })
        });

        if (!refreshResponse.ok) {
            throw new Error("Token refresh failed");
        }

        const data = await refreshResponse.json();
        const newAccessToken = data.access;
        localStorage.setItem('access_token', newAccessToken);

        if (data.refresh) {
            localStorage.setItem('refresh_token', data.refresh);
        }

        processQueue(null, newAccessToken);

        // Retry original fetch request
        config = config || {};
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${newAccessToken}`
        };
        
        return originalFetch(resource, config);

    } catch (refreshError) {
        processQueue(refreshError, null);
        handleLogout();
        return response; // Return the original 401 response if we can't refresh
    } finally {
        isRefreshing = false;
    }
  }

  return response;
};
